/**
 * Aggregates decrypted device activity for display. Decryption happens once
 * per fetch and is cached by the caller; aggregation is a separate, cheap,
 * pure step so re-slicing by a different time period (paging through days,
 * switching to week/month) never needs to touch the Hub or the private key
 * again.
 */
import { classifyDeviceEvents } from '~/util/appCatalog';
import { eventsWithTimestamps } from '~/util/deviceTimeline';
import { dedupeIntervals } from '~/util/intervalPayload';
import { decryptVerifiedDay, decryptVerifiedSummary, NotYetVerified, RejectedRecord, } from '~/util/recordTrust';
import { seconds_to_duration } from '~/util/time';
/** Adds up the counts in `results`, skipping devices not loaded yet. */
export function decryptStatusOf(results) {
    const failedKeyVersions = new Set();
    const totals = { failedCount: 0, unsignedCount: 0, rejectedCount: 0, unverifiedCount: 0 };
    for (const result of results) {
        if (!result)
            continue;
        totals.failedCount += result.failedCount;
        totals.unsignedCount += result.unsignedCount ?? 0;
        totals.rejectedCount += result.rejectedCount ?? 0;
        totals.unverifiedCount += result.unverifiedCount ?? 0;
        result.failedKeyVersions.forEach(v => failedKeyVersions.add(v));
    }
    return { ...totals, failedKeyVersions: Array.from(failedKeyVersions).sort((a, b) => a - b) };
}
/**
 * Decrypts every record with `secret` and checks each against the device's
 * signing key (see recordTrust). Failures are tolerated per record rather
 * than aborting the batch: a key that's right for a device's *current* key
 * version still legitimately fails on records from before a rotation, and
 * a rejected record only removes itself. Also returns each kept record as
 * verified, for sealing days from.
 */
export async function decryptRecordsDetailed(records, secret, trust) {
    const verified = [];
    let failedCount = 0;
    let rejectedCount = 0;
    let unverifiedCount = 0;
    const failedKeyVersions = new Set();
    for (const record of records) {
        try {
            verified.push(await decryptVerifiedSummary(secret, record, trust));
        }
        catch (err) {
            if (err instanceof NotYetVerified) {
                unverifiedCount++;
            }
            else if (err instanceof RejectedRecord) {
                rejectedCount++;
            }
            else {
                failedCount++;
                failedKeyVersions.add(record.key_version);
            }
        }
    }
    return {
        // Records are one device's, so interval ids are unique across them.
        payloads: dedupeIntervals(verified.map(v => v.payload)),
        failedCount,
        failedKeyVersions: Array.from(failedKeyVersions).sort((a, b) => a - b),
        unsignedCount: verified.filter(v => v.status === 'unsigned').length,
        rejectedCount,
        unverifiedCount,
        verified,
    };
}
export async function decryptRecords(records, secret, trust) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { verified: _verified, ...result } = await decryptRecordsDetailed(records, secret, trust);
    return result;
}
/**
 * Decrypts and checks sealed days like `decryptRecords` does summaries,
 * tolerating failures per day. `readableDays` lists the days that decrypted
 * and passed; a day that didn't is no better than an unsealed one, and
 * callers treat it that way.
 */
export async function decryptDayBundles(bundles, secret, trust) {
    const payloads = [];
    const readableDays = new Set();
    let failedCount = 0;
    let rejectedCount = 0;
    let unverifiedCount = 0;
    let unsignedCount = 0;
    const failedKeyVersions = new Set();
    for (const bundle of bundles) {
        try {
            const records = await decryptVerifiedDay(secret, bundle, trust);
            payloads.push(...records.map(r => r.payload));
            unsignedCount += records.filter(r => r.status === 'unsigned').length;
            readableDays.add(bundle.day);
        }
        catch (err) {
            if (err instanceof NotYetVerified) {
                unverifiedCount++;
            }
            else if (err instanceof RejectedRecord) {
                rejectedCount++;
            }
            else {
                failedCount++;
                failedKeyVersions.add(bundle.key_version);
            }
        }
    }
    return {
        payloads: dedupeIntervals(payloads),
        failedCount,
        failedKeyVersions: Array.from(failedKeyVersions).sort((a, b) => a - b),
        unsignedCount,
        rejectedCount,
        unverifiedCount,
        readableDays,
    };
}
/** Folds already-decrypted payloads into one display summary. Pure and
 * synchronous — safe to call on every period-navigation click. */
export function summarizePayloads(payloads) {
    const secondsByApp = new Map();
    let activeSeconds = 0;
    let afkSeconds = 0;
    let earliest = null;
    let latest = null;
    for (const payload of payloads) {
        earliest = earliest === null ? payload.window_start : Math.min(earliest, payload.window_start);
        latest = latest === null ? payload.window_end : Math.max(latest, payload.window_end);
        for (const event of payload.events) {
            if (event.afk) {
                afkSeconds += event.duration_secs;
                continue;
            }
            activeSeconds += event.duration_secs;
            const app = event.app ?? 'Unknown';
            const entry = secondsByApp.get(app) ?? {
                seconds: 0,
                typingSeconds: 0,
                mouseSeconds: 0,
                mouseWithoutKeyboardSeconds: 0,
                inputObserved: false,
                mouseWithoutKeyboardObserved: false,
            };
            entry.seconds += event.duration_secs;
            if (typeof event.keyboard_active_seconds === 'number') {
                entry.typingSeconds += event.keyboard_active_seconds;
                entry.inputObserved = true;
            }
            if (typeof event.mouse_active_seconds === 'number') {
                entry.mouseSeconds += event.mouse_active_seconds;
                entry.inputObserved = true;
            }
            if (typeof event.mouse_without_keyboard_seconds === 'number') {
                entry.mouseWithoutKeyboardSeconds += event.mouse_without_keyboard_seconds;
                entry.mouseWithoutKeyboardObserved = true;
            }
            secondsByApp.set(app, entry);
        }
    }
    const totalAppSeconds = Math.max(activeSeconds, 1);
    const byApp = Array.from(secondsByApp.entries())
        .map(([app, input]) => ({
        app,
        seconds: input.seconds,
        duration: seconds_to_duration(input.seconds),
        percent: Math.round((input.seconds / totalAppSeconds) * 100),
        typingSeconds: input.inputObserved ? input.typingSeconds : null,
        mouseSeconds: input.inputObserved ? input.mouseSeconds : null,
        mouseWithoutKeyboardSeconds: input.mouseWithoutKeyboardObserved
            ? input.mouseWithoutKeyboardSeconds
            : null,
    }))
        .sort((a, b) => b.seconds - a.seconds);
    return {
        batchCount: payloads.length,
        earliest,
        latest,
        byApp,
        activeDuration: seconds_to_duration(activeSeconds),
        afkDuration: seconds_to_duration(afkSeconds),
        activeSeconds,
        afkSeconds,
    };
}
/** The payloads' events that fall in `[startUnix, endUnix)`, cut to it.
 * Each event is placed by its own time (as `deviceTimeline.ts` does), and an
 * event straddling the edge is cut there, its input seconds scaled with it,
 * so a payload that spans two days (an agent-sealed day is one payload for a
 * UTC day, which is parts of two Melbourne days) counts in each day only
 * what happened in it. Payloads with nothing inside the range are dropped;
 * those kept carry `start` on every event, so later placement is exact. */
export function payloadsInRange(payloads, startUnix, endUnix) {
    const out = [];
    for (const p of payloads) {
        if (p.window_start >= endUnix || p.window_end <= startUnix)
            continue;
        const placed = eventsWithTimestamps(p);
        const events = [];
        placed.forEach((e, i) => {
            const from = Math.max(e.start, startUnix);
            const to = Math.min(e.start + e.duration, endUnix);
            if (to <= from)
                return;
            const original = p.events[i];
            const kept = to - from;
            if (kept === e.duration) {
                events.push(original.start === undefined ? { ...original, start: e.start } : original);
                return;
            }
            const share = kept / e.duration;
            const scaled = (v) => (typeof v === 'number' ? Math.round(v * share) : v);
            events.push({
                ...original,
                start: from,
                duration_secs: kept,
                keyboard_active_seconds: scaled(original.keyboard_active_seconds),
                mouse_active_seconds: scaled(original.mouse_active_seconds),
                mouse_without_keyboard_seconds: scaled(original.mouse_without_keyboard_seconds),
            });
        });
        if (events.length)
            out.push({ ...p, events });
    }
    return out;
}
/** Flattens decrypted payloads' active (non-AFK) events into the shape the
 * existing category classifier (`classifyEvents`, shared with the local
 * ActivityWatch query pipeline) expects. Each event carries its own time
 * (reconstructed as `deviceTimeline.ts` does), not the payload's start: the
 * engine's labels belong to a client day, and a payload can straddle two. */
function toActiveIEvents(payloads) {
    const events = [];
    for (const payload of payloads) {
        for (const event of eventsWithTimestamps(payload)) {
            if (event.afk)
                continue;
            events.push({
                timestamp: new Date(event.start * 1000).toISOString(),
                duration: event.duration,
                data: {
                    app: event.app ?? '',
                    title: event.title ?? '',
                    recorded_app: event.recorded_app,
                    bundle_id: event.bundle_id ?? null,
                    // For the engine's labels, which belong to a computer, a day and a site.
                    device: payload.device_id ?? null,
                    domain: event.domain ?? null,
                },
            });
        }
    }
    return events;
}
const UNCATEGORIZED = ['Uncategorized'];
/** True when `cat` is `filter` itself or one of its descendants — mirrors
 * the local Activity page's category-filter semantics (a filter on "Work"
 * also matches "Work > Programming"). */
export function isCategoryOrDescendant(cat, filter) {
    return cat.length >= filter.length && filter.every((segment, i) => cat[i] === segment);
}
/** Classifies every active event against `categories` (the same rule set
 * the rest of the app uses, from `categoryStore.classes`) and rolls up
 * total time per top-level-through-leaf category path. `getColor` is
 * `categoryStore.get_category_color` — passed in rather than imported so
 * this module stays store-free and independently testable. */
export function summarizeCategories(payloads, categories, getColor, categoryFilter = null) {
    const classified = classifyDeviceEvents(toActiveIEvents(payloads), categories);
    const secondsByCategory = new Map();
    for (const event of classified) {
        const cat = event.data.$category ?? UNCATEGORIZED;
        if (categoryFilter && !isCategoryOrDescendant(cat, categoryFilter))
            continue;
        const key = cat.join('>');
        const existing = secondsByCategory.get(key);
        if (existing) {
            existing.seconds += event.duration;
        }
        else {
            secondsByCategory.set(key, { name: cat, seconds: event.duration });
        }
    }
    const total = Math.max(Array.from(secondsByCategory.values()).reduce((acc, c) => acc + c.seconds, 0), 1);
    return Array.from(secondsByCategory.values())
        .map(({ name, seconds }) => ({
        category: name,
        name: name.join(' > '),
        seconds,
        duration: seconds_to_duration(seconds),
        percent: Math.round((seconds / total) * 100),
        color: getColor(name),
    }))
        .sort((a, b) => b.seconds - a.seconds);
}
/** Same app-breakdown shape as `summarizePayloads().byApp`, but narrowed to
 * events whose classified category is `categoryFilter` or a descendant of
 * it — the category-filter dropdown's effect on "Time by application". */
export function summarizeAppsForCategory(payloads, categories, categoryFilter) {
    const classified = classifyDeviceEvents(toActiveIEvents(payloads), categories);
    const secondsByApp = new Map();
    for (const event of classified) {
        const cat = event.data.$category ?? UNCATEGORIZED;
        if (!isCategoryOrDescendant(cat, categoryFilter))
            continue;
        const app = event.data.app || 'Unknown';
        secondsByApp.set(app, (secondsByApp.get(app) ?? 0) + event.duration);
    }
    const total = Math.max(Array.from(secondsByApp.values()).reduce((a, b) => a + b, 0), 1);
    return Array.from(secondsByApp.entries())
        .map(([app, seconds]) => ({
        app,
        seconds,
        duration: seconds_to_duration(seconds),
        percent: Math.round((seconds / total) * 100),
        // Category filtering operates on the compatibility IEvent shape, which
        // intentionally omits input extensions. Avoid implying a zero here.
        typingSeconds: null,
        mouseSeconds: null,
        mouseWithoutKeyboardSeconds: null,
    }))
        .sort((a, b) => b.seconds - a.seconds);
}
